/// 金額 (branded 型 + 非負バリデーション)。
///
/// 対象フィールド:
///   - `shared/types/contract.ts`: `monthly_amount` / `initial_amount` /
///     `annual_contract_amount` / `proposed_acv` / `proposed_mrr` / `spot_amount`
///   - `shared/types/opportunity.ts`: `expected_monthly_amount` /
///     `expected_initial_amount` / `confirmed_monthly_amount`
///
/// API の Decimal は **文字列** で来る (精度欠落を避けるため number 化しない)。
/// よってこの VO も string ベースで保持し、**正規化せず verbatim 保持**する。
///
/// バリデーション規則は backend `Money` VO (`domain/src/core/value_objects.rs`) と
/// 揃える: 金額は **非負** (>= 0)。precision/scale は呼び出し側 / DB カラムの責務で、
/// この VO は符号不変条件のみを所有する (issue #468 / #430)。

/** 検証済みの非負金額 (Decimal 文字列、verbatim 保持)。 */
export type MoneyAmount = string & { readonly __brand: 'MoneyAmount' };

/**
 * `value` を検証し、問題なければ `MoneyAmount` 型で返す。
 *
 * 無効な場合は `null` を返す (例外は投げない)。
 *
 * - 空文字 / whitespace / 制御文字を含む → null
 * - 十進数としてパースできない (非数値・余分な文字) → null
 * - 負数 → null
 * - `NaN` / `Infinity` 相当 → null
 * - 妥当な非負の十進数文字列なら、そのまま (verbatim) `MoneyAmount` で返す。
 */
export function createMoneyAmount(value: string): MoneyAmount | null {
  const parsed = parseNonNegativeDecimal(value);
  if (parsed === null) {
    return null;
  }
  return value as MoneyAmount;
}

/**
 * 厳密な十進数文字列のみを受け付ける正規表現。
 *
 * - 任意の先頭符号 (`+` / `-`) + 整数部 + 任意の小数部。
 * - `1e5` のような指数表記、`Infinity`、`NaN`、`.5`、`5.`、空文字は弾く。
 * - whitespace / 制御文字を含む文字列は `^` / `$` で弾かれる。
 */
const DECIMAL_RE = /^[+-]?[0-9]+(\.[0-9]+)?$/;

/**
 * `value` を厳密な十進数文字列として検証し、非負なら数値を返す。
 *
 * 範囲外 (負数) / パース不能 / 非有限値はすべて `null`。
 */
function parseNonNegativeDecimal(value: string): number | null {
  if (!DECIMAL_RE.test(value)) {
    return null;
  }
  const num = Number(value);
  // `DECIMAL_RE` を通った時点で NaN/Infinity にはならないが、防御的に確認する。
  if (!Number.isFinite(num)) {
    return null;
  }
  if (num < 0) {
    return null;
  }
  return num;
}
